// Shared marketing top nav (landing, platform, …). Locale-aware: links
// resolve to the current locale, and the language switcher keeps you on
// the same `page` when you switch. `page` is the locale-agnostic suffix
// ("" home, "platform", …) — used by the switcher and to mark the
// active link.

import { Link } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { LangSwitcher } from "@/components/ui/LangSwitcher";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { localePath, type PageKey } from "@/i18n/config";
import { useDict, useLocale } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";

export function SiteNav({ page = "" }: { page?: PageKey | "" }) {
  const locale = useLocale();
  const { nav } = useDict();
  const isAuthed = Boolean(useAuthStore((s) => s.accessToken));
  const home = localePath(locale);
  const link = (active: boolean) =>
    active ? "text-content no-underline" : "text-muted no-underline";

  return (
    <nav
      className="sticky top-0 z-20 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3"
      style={{
        background: "rgb(var(--color-bg-deep) / 0.88)",
        backdropFilter: "blur(8px)",
        borderBottom: "1px solid rgb(var(--color-stroke))",
      }}
    >
      <Link
        to={home}
        className="flex items-center gap-2 sm:gap-3 no-underline shrink-0 min-w-0"
      >
        <img
          src="/logo.png"
          alt="TrueHacking logo"
          width={200}
          height={200}
          className="h-9 sm:h-10 w-auto"
        />
        <div className="hidden sm:block font-display text-[18px] font-bold text-accent tracking-[0.12em]">
          TrueHacking
        </div>
      </Link>
      <div className="hidden md:flex items-center gap-6 font-mono text-[12px]">
        <Link to={localePath(locale, "platform")} className={link(page === "platform")}>
          {nav.platform}
        </Link>
        <Link to={localePath(locale, "pricing")} className={link(page === "pricing")}>
          {nav.pricing}
        </Link>
        <Link to={localePath(locale, "blog")} className={link(page === "blog")}>
          {nav.blog}
        </Link>
        <a href="mailto:support@truehacking.ai" className={link(false)}>
          {nav.contact}
        </a>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <LangSwitcher page={page} className="hidden sm:flex" />
        <ThemeToggle />
        {isAuthed ? (
          <Link to="/app">
            <Button variant="primary">{nav.goToApp}</Button>
          </Link>
        ) : (
          <>
            <Link to="/login" className="hidden sm:inline-flex">
              <Button variant="default">{nav.login}</Button>
            </Link>
            <Link to="/login">
              <Button variant="primary">{nav.startFree}</Button>
            </Link>
          </>
        )}
      </div>
    </nav>
  );
}
